import { FeatureDefinition } from "../api";

const WORKFLOW_STEPS = [
  { key: "interview",   label: "Expert Interview",     desc: "Talk to a domain expert live", tabs: ["interview"] },
  { key: "extraction",  label: "Variable Extraction",  desc: "Structured signals from the conversation", tabs: ["results"] },
  { key: "calibration", label: "SHELF Calibration",    desc: "Multi-expert consensus", tabs: ["elicitation"] },
  { key: "scan",        label: "Data Scan",            desc: "Match signals to your dataset", tabs: ["audit", "variables"] },
  { key: "resolution",  label: "Data Resolution",      desc: "Map expert variables to columns", tabs: ["resolution"] },
  { key: "engineering", label: "Feature Engineering",   desc: "Create new features from thresholds", tabs: ["staging"] },
  { key: "model",       label: "Model Impact",         desc: "Before/after comparison", tabs: ["pipeline", "performance"] },
  { key: "export",      label: "Export & Report",       desc: "Download dataset and notebook", tabs: ["review", "registry"] },
];

function getActiveIdx(tab: string): number {
  const idx = WORKFLOW_STEPS.findIndex(s => s.tabs.includes(tab));
  return idx >= 0 ? idx : -1;
}

interface Props {
  feature: FeatureDefinition | null;
  activeTab: string;
  pipelineStep?: number;
  onNavigate?: (tab: string, panel?: string) => void;
}

export default function WorkflowTracker({ feature, activeTab, onNavigate }: Props) {
  const current = getActiveIdx(activeTab);

  return (
    <div style={{
      width: 220, flexShrink: 0, display: "flex", flexDirection: "column",
      padding: "20px 16px", background: "#fff", borderLeft: "1px solid rgba(0,0,0,0.06)",
      overflowY: "auto", height: "100%",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid rgba(0,0,0,0.06)",
      }}>
        <div style={{
          width: 20, height: 20, borderRadius: "50%",
          background: "linear-gradient(135deg, #1e3a6e, #3b7dd8)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
            <path d="M8 1L9.5 6.5L15 8L9.5 9.5L8 15L6.5 9.5L1 8L6.5 6.5L8 1Z" fill="#fff" />
          </svg>
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#111" }}>Workflow</span>
      </div>

      {/* Steps */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2, position: "relative", paddingLeft: 4 }}>
        {/* Connecting line */}
        <div style={{
          position: "absolute", left: 13, top: 14, bottom: 14, width: 1.5,
          background: "rgba(30,58,110,0.1)", zIndex: 0,
        }} />

        {WORKFLOW_STEPS.map((step, i) => {
          const isActive = i === current;
          const isComplete = current >= 0 && i < current;
          const isPending = current < 0 || i > current;

          return (
            <div
              key={step.key}
              onClick={() => onNavigate?.(step.tabs[0])}
              style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "7px 6px", borderRadius: 8, cursor: "pointer",
                background: isActive ? "rgba(30,58,110,0.04)" : "transparent",
                transition: "background 0.15s ease",
                position: "relative", zIndex: 1,
              }}
            >
              {/* Circle */}
              <div style={{
                width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                background: isActive
                  ? "linear-gradient(135deg, #1e3a6e, #3b7dd8)"
                  : isComplete
                    ? "linear-gradient(135deg, #1e3a6e, #3b7dd8)"
                    : "#fff",
                border: isPending ? "1.5px dashed rgba(30,58,110,0.25)" : "2px solid transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, fontWeight: 700,
                color: isActive || isComplete ? "#fff" : "rgba(30,58,110,0.4)",
                boxShadow: isActive ? "0 0 0 3px rgba(30,58,110,0.12)" : "none",
                transition: "all 0.25s ease",
              }}>
                {isComplete ? "✓" : i + 1}
              </div>

              {/* Label */}
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: 11, fontWeight: isActive ? 700 : 500,
                  color: isActive ? "#1e3a6e" : isPending ? "rgba(0,0,0,0.35)" : "#111",
                  lineHeight: 1.3,
                }}>
                  {step.label}
                </div>
                <div style={{
                  fontSize: 9, color: isActive ? "rgba(30,58,110,0.6)" : "rgba(0,0,0,0.3)",
                  lineHeight: 1.3, marginTop: 1,
                }}>
                  {step.desc}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Current feature info */}
      {feature && (
        <div style={{
          marginTop: "auto", paddingTop: 16, borderTop: "1px solid rgba(0,0,0,0.06)",
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.3)", marginBottom: 6 }}>
            Active variable
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#1e3a6e" }}>{feature.name}</div>
          <div style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", marginTop: 2 }}>
            Prior: N({feature.bayesian_prior.mean.toFixed(2)}, {feature.bayesian_prior.std_dev.toFixed(2)})
          </div>
        </div>
      )}
    </div>
  );
}
